import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, map, Observable, Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { PusherService } from "./pusher.service";
import { SecureStorageService } from "./secure-storage.service";

export interface ChatMessage {
  id: number;
  user_id: number;
  stream_id: string;
  message: string;
  created_at: string;
  user: {
    name?: string;
    prenom?: string;
    profile_picture?: string;
  };
  status?: "sending" | "sent" | "error";
  type?: "text" | "sticker";
  sticker_name?: string;
  sticker_id?: string;
}

export interface ChatState {
  enabled: boolean;
  error?: string;
}

@Injectable({
  providedIn: "root",
})
export class ChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private chatEnabledSubject = new BehaviorSubject<boolean>(true);
  private chatStateSubject = new BehaviorSubject<ChatState>({ enabled: true });
  private typingUsersSubject = new BehaviorSubject<{
    [streamId: string]: string[];
  }>({});
  private newMessageSubject = new Subject<ChatMessage>();
  private chatStates: { [streamId: string]: BehaviorSubject<boolean> } = {};

  private baseApiUrl = environment.apiUrl;
  private token: string = "";
  private httpOptions: { headers: HttpHeaders };

  constructor(
    private http: HttpClient,
    private pusherService: PusherService,
    private secureStorage: SecureStorageService
  ) {
    const rawToken = this.secureStorage.getItem<string>("token");
    this.token = rawToken ? rawToken.replace(/"/g, "") : "";
    this.httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      }),
    };

    // Écouter les changements de localStorage entre les onglets
    window.addEventListener("storage", (event) => {
      if (event.key && event.key.startsWith("chat_state_")) {
        const streamId = event.key.replace("chat_state_", "");
        const newState = event.newValue === "true";

        if (this.chatStates[streamId]) {
          this.chatStates[streamId].next(newState);
        }
      }
    });
  }

  setChatState(streamId: string, isEnabled: boolean): Observable<any> {
    return this.http.post(
      `${this.baseApiUrl}/streams/${streamId}/chat-state`,
      { chat_enabled: isEnabled },
      this.httpOptions
    );
  }

  listenToChatState(streamId: string): void {
    const channelName = `private-chat.stream.${streamId}`;

    console.log("🎯 Écoute des événements chat state pour:", streamId);

    this.pusherService.subscribeToChannel(channelName);

    // Écouter l'événement de changement d'état du chat
    this.pusherService.bindEvent(
      channelName,
      "ChatStateChanged",
      (data: any) => {
        console.log("🎯 Événement ChatStateChanged reçu:", data);
        this.chatEnabledSubject.next(data.chat_enabled);
      }
    );
  }

  getChatState$(): Observable<boolean> {
    return this.chatEnabledSubject.asObservable();
  }

  getInitialChatState(streamId: string): Observable<boolean> {
    return this.http
      .get<{ chat_enabled: boolean }>(
        `${this.baseApiUrl}/streams/${streamId}/chat-state`,
        this.httpOptions
      )
      .pipe(map((response) => response.chat_enabled));
  }

  // S'abonner au chat d'un stream
  subscribeToStreamChat(streamId: string): void {
    const channelName = `private-chat.stream.${streamId}`;

    this.pusherService.subscribeToChannel(channelName);

    // Écouter les nouveaux messages
    this.pusherService.bindEvent(
      channelName,
      "NewApiChatMessage",
      (data: any) => {
        const message: ChatMessage = {
          id: data.id,
          user_id: data.user_id,
          stream_id: streamId,
          message: data.message,
          created_at: data.created_at,
          type: data.type || "text",
          sticker_name: data.sticker_name,
          sticker_id: data.sticker_id,
          user: {
            name: data.user.name,
            prenom: data.user.prenom,
            profile_picture: data.user.profile_picture,
          },
        };
        this.newMessageSubject.next(message);
      }
    );

    // Écouter les changements d'état du chat
    this.pusherService.bindEvent(
      channelName,
      "ChatStateChanged",
      (data: any) => {
        console.log("💬 Événement état chat reçu:", data);
        this.chatStateSubject.next({ enabled: data.chat_enabled });
      }
    );
  }

  setChatState2(streamId: string, isEnabled: boolean): void {
    // Sauvegarder dans localStorage pour la synchronisation entre onglets
    localStorage.setItem(`chat_state_${streamId}`, isEnabled.toString());

    // Mettre à jour le BehaviorSubject
    if (!this.chatStates[streamId]) {
      this.chatStates[streamId] = new BehaviorSubject<boolean>(true);
    }
    this.chatStates[streamId].next(isEnabled);
  }

  getChatState3(streamId: string): Observable<boolean> {
    if (!this.chatStates[streamId]) {
      // Récupérer l'état depuis localStorage au début
      const savedState = localStorage.getItem(`chat_state_${streamId}`);
      const initialState = savedState ? savedState === "true" : true;

      this.chatStates[streamId] = new BehaviorSubject<boolean>(initialState);
    }
    return this.chatStates[streamId].asObservable();
  }

  // Se désabonner du chat d'un stream
  unsubscribeFromStreamChat(streamId: string): void {
    this.pusherService.unsubscribeFromChannel(
      `private-chat.stream.${streamId}`
    );
  }

  // Obtenir les messages d'un stream
  getMessages(streamId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(
      `${this.baseApiUrl}/streams/${streamId}/messages`,
      this.httpOptions
    );
  }

  // Envoyer un message
  sendMessage(
    streamId: string,
    message: string,
    type: "text" | "sticker" = "text",
    stickerData?: { name?: string; id?: string }
  ): Promise<any> {
    const payload: any = { message, type };

    if (type === "sticker" && stickerData) {
      payload.sticker_name = stickerData.name;
      payload.sticker_id = stickerData.id;
    }

    return this.http
      .post(
        `${this.baseApiUrl}/streams/${streamId}/messages`,
        payload,
        this.httpOptions
      )
      .toPromise();
  }

  debugChatState(streamId: string): void {
    console.log("=== DEBUG CHAT STATE ===");
    console.log("Stream ID:", streamId);
    console.log(
      "LocalStorage:",
      localStorage.getItem(`chat_state_${streamId}`)
    );
    console.log("BehaviorSubject exists:", !!this.chatStates[streamId]);
    if (this.chatStates[streamId]) {
      console.log("Current value:", this.chatStates[streamId].getValue());
    }
    console.log("========================");
  }

  // Définir l'état de frappe d'un utilisateur
  setTyping(streamId: string, isTyping: boolean): void {
    // Implémenter la logique pour informer les autres utilisateurs que l'utilisateur est en train de taper
    // via Pusher
    if (isTyping) {
      this.pusherService.triggerEvent("presence-typing", "typing", {
        streamId,
      });
    } else {
      this.pusherService.triggerEvent("presence-typing", "stopped-typing", {
        streamId,
      });
    }
  }

  // Obtenir l'état du chat
  getChatState(): Observable<ChatState> {
    return this.chatStateSubject.asObservable();
  }

  // Obtenir les utilisateurs en train de taper
  onTypingUsers(): Observable<{ [streamId: string]: string[] }> {
    return this.typingUsersSubject.asObservable();
  }

  // Obtenir les nouveaux messages
  onNewMessage(): Observable<ChatMessage> {
    return this.newMessageSubject.asObservable();
  }

  // Retirer un participant du live
  removeParticipant(payload: {
    slug: string;
    user_id: number;
  }): Observable<any> {
    return this.http.post(
      `${this.baseApiUrl}/streams/put-out`,
      payload,
      this.httpOptions
    );
  }
}
